import { Reducer } from 'react'
import { useCallback, useEffect, useReducer, useState } from 'react'

interface ScrollState {
  scrolled: boolean
  atTop: boolean
  atBottom: boolean
}
interface ScrollReducerAction {
  type: 'update'
  data: Partial<ScrollState>
}

const scrollPositionReduce: Reducer<ScrollState, ScrollReducerAction> = (
  state: ScrollState,
  action: ScrollReducerAction
): ScrollState => {
  if (action.type === 'update') {
    return {
      ...state,
      ...action.data,
    }
  } else throw new Error('Unknown action')
}

export function useRecordScrollPosition() {
  const [state, dispatch] = useReducer(scrollPositionReduce, {
    scrolled: false,
    atTop: true,
    atBottom: false,
  })

  const recordScrollPosition = useCallback<EventListener>(
    (e) => {
      const target = e.currentTarget as HTMLDivElement
      const scrollTop = target.scrollTop
      const scrollEndPosition = target.scrollHeight - target.offsetHeight

      const updates: Partial<ScrollState> = {}

      if (scrollTop === 0 && state.scrolled) updates.scrolled = false
      else if (scrollTop > 0 && !state.scrolled) updates.scrolled = true

      if (scrollTop === 0 && !state.atTop) updates.atTop = true
      else if (scrollTop > 0 && state.atTop) updates.atTop = false

      if (scrollTop >= scrollEndPosition && !state.atBottom) updates.atBottom = true
      else if (scrollTop < scrollEndPosition && state.atBottom) updates.atBottom = false

      if (Object.keys(updates).length > 0) dispatch({ type: 'update', data: updates })
    },
    [state]
  )

  return {
    ...state,
    recordScrollPosition,
  }
}

export function useScrollListener() {
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null)

  const { scrolled, atTop, atBottom, recordScrollPosition } = useRecordScrollPosition()

  useEffect(() => {
    if (scrollContainer) {
      recordScrollPosition({ currentTarget: scrollContainer } as unknown as Event)
      scrollContainer.addEventListener('scroll', recordScrollPosition)
      return () => scrollContainer?.removeEventListener('scroll', recordScrollPosition)
    }
  }, [recordScrollPosition, scrollContainer])

  return {
    scrolled,
    atTop,
    atBottom,
    listener: setScrollContainer,
  }
}

export function useScrollPosition(elementQuery: string = '#app-main') {
  const { scrolled, atTop, atBottom, recordScrollPosition } = useRecordScrollPosition()

  useEffect(() => {
    const element = document.querySelector(elementQuery)
    if (element) {
      recordScrollPosition({ currentTarget: element } as unknown as Event)
      element.addEventListener('scroll', recordScrollPosition)
    }

    return () => element?.removeEventListener('scroll', recordScrollPosition)
  }, [elementQuery, recordScrollPosition])

  return {
    scrolled,
    atTop,
    atBottom,
  }
}
